import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
    Box,
    Flex,
    Select,
    SkeletonText,
    Text,
    Tooltip,
    useToast
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useGetPixelsByAdaccountQuery } from 'store/BmAccounts/bmAccountApi';
import { useGetCustomConversionEventsQuery, useGetCustomStatsQuery } from 'store/campaigns/adsetFormApi';

const DataSetPixelSelector = ({
    adsAccount,
    promotedObjectFields,
    promotedObjectData,
    onSelectedPixelId,
    onSelectedEventType,
    onBlur,
    isSaving,
    adSetFormik
}) => {

    const toast = useToast();

    const { adsAccountId, adsAccountName } = adsAccount || {};

    const [selectedPixelId, setSelectedPixelId] = useState(promotedObjectData?.pixel_id);
    const [selectedEventType, setSelectedEventType] = useState(
        promotedObjectData?.custom_event_str ? promotedObjectData?.custom_event_str :
            promotedObjectData?.custom_conversion_id
                ? promotedObjectData.custom_conversion_id
                : promotedObjectData?.custom_event_type || ''
    );

    const [pixelIdError, setPixelIdError] = useState(true);
    const [eventTypeError, setEventTypeError] = useState(true);

    const { data: pixelList, isLoading: isPixelLoading } = useGetPixelsByAdaccountQuery(
        {
            adsAccountId,
        }, {}
    );

    const { data: CustomeventList, isLoading:isCustomListLoading, error:isCustomEventError } = useGetCustomConversionEventsQuery({
        adsAccountId,
    }, {});

    const { data: StatusList, isLoading:isStatusListLoading, error:isStatusError } = useGetCustomStatsQuery({
        pixelId: selectedPixelId,
        adsAccountId,
    }, { skip: !selectedPixelId });

    const eventTypeOptions = promotedObjectFields.custom_event_type?.options || [];

    // useEffect(() => {
    //     if (isCustomEventError) {
    //             toast({
    //             position: "top-right",
    //             title: "Error",
    //             description: isCustomEventError?.data?.message,
    //             status: "error",
    //             duration: 4000,
    //             isClosable: true,
    //         });
    //     }
    // }, [isCustomEventError]);

    useEffect(() => {
        if (isStatusError) {
                toast({
                position: "top-right",
                title: "Error",
                description: isStatusError?.data?.message,
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        }
    }, [isStatusError]);

    // This function filters and updates the eventTypeOptions
    const filterEventsByPixel = () => {
        if (selectedPixelId || CustomeventList?.length > 0 || StatusList?.length > 0) {
            const eventTypeFilteredOptions = eventTypeOptions?.map(eventType => ({
                ...eventType,
                category: 'Standard',
            }));
        
            const filteredEvents = (Array.isArray(CustomeventList) ? CustomeventList : [])
                .map(event => ({
                    ...event,
                    category: 'Custom',
                }))
                .filter(event => event?.pixel === selectedPixelId);
        
            const statusEvents = (Array.isArray(StatusList) ? StatusList : [])
                .map(status => ({
                    eventName: status.eventName,
                    label: status.label,
                    value: status.value,
                    available: status.available === undefined ? true : status.available,
                    category: 'Status',
                })) || [];
        
            const uniqueStatusEvents = statusEvents.filter(statusEvent =>
                !eventTypeOptions.some(option => option.eventName === statusEvent.eventName)
            );
        
            return [...uniqueStatusEvents, ...filteredEvents, ...eventTypeFilteredOptions];
        }
    };

    const handlePixelIdChange = (event) => {
        const selectedId = event.target.value;
        setPixelIdError(!selectedId);
        setSelectedPixelId(selectedId);
        onSelectedPixelId(selectedId, adsAccountId);

        setSelectedEventType('');
        setEventTypeError(true);
    };

    const handleEventTypeChange = (event) => {
        const selectedType = event.target.value;
        setEventTypeError(!selectedType);
        setSelectedEventType(selectedType);

        const selectedOption = event.target.selectedOptions[0]; // get the selected option
        const selectedEventOption = JSON.parse(selectedOption.getAttribute('data-option')); // parse the stored eventOption object

        let optionToSend = {};
        if(selectedEventOption?.category === 'Custom'){
            optionToSend = {
                pixel_rule: selectedEventOption?.pixel_rule,
                custom_conversion_id: selectedEventOption?.custom_conversion_id,
                custom_event_type: selectedEventOption?.value,
            }
        }else if (selectedEventOption?.category === 'Status'){
            optionToSend = {
                custom_event_type: selectedEventOption?.eventName,
                custom_event_str: selectedEventOption?.value,
            }
        }else{
            optionToSend = {
                custom_event_type: selectedEventOption?.value,
            }
        }

        if (selectedEventOption) {
            onSelectedEventType(optionToSend, adsAccountId);
        }else{
            const customTypeEnd = {
                custom_event_type: selectedType,
            }
            onSelectedEventType(customTypeEnd, adsAccountId);
        }
    };

    return (
        <Box mb={'10px'}>
            <Text fontWeight={'bold'} mb={2}>Ad Account: {adsAccountName}</Text>
            {isPixelLoading ? (
                <Flex>
                    <SkeletonText width="80%" noOfLines={2} skeletonHeight={2} />
                </Flex>
            ) : pixelList?.length > 0 ? (
                <Flex gap={'20px'} mb={2}>
                    <Box flex={1}>
                        <Flex alignItems={'center'} mb={'5px'}>
                            <Text htmlFor="pixel-id">Dataset</Text>
                            <Text display="inline-flex" fontSize="18px" color="red.400" mr={2}>*</Text>
                            <Tooltip
                                borderRadius={5}
                                label={
                                    <Box fontSize="sm" p={'5px'}>
                                        <Text>
                                            <strong>About the Meta pixel</strong> <br></br>
                                            The Meta pixel is a piece of code that you add to your website to track conversions from your ads.<br></br>
                                            <strong>Can't find a dataset ID?</strong><br></br>
                                            Only pixel IDs associated with this ad account are shown here. If you can't find a pixel ID, it may not be associated with this account. Go to Meta Business Manager to share the pixel with this account.
                                        </Text>
                                    </Box>
                                }
                                fontSize="md"
                            >
                                <InfoOutlineIcon pointerEvents="all" />
                            </Tooltip>
                        </Flex>
                        <Select
                            id="pixel-id"
                            value={selectedPixelId}
                            placeholder="Select a dataset"
                            onChange={handlePixelIdChange}
                            size={'sm'}
                            borderRadius={'md'}
                            onBlur={onBlur}
                        >
                            {pixelList?.map((pixel) => (
                                <option key={pixel.id} value={pixel.id}>
                                    {pixel.name}
                                </option>
                            ))}
                        </Select>

                        {pixelIdError && isSaving && !selectedPixelId && (
                            <Text color="red.500" fontSize="sm" mt={1}>
                                Dataset is required.
                            </Text>
                        )}

                    </Box>

                    <Box flex={1}>
                        <Flex alignItems={'center'} mb={'5px'}>
                            <Text htmlFor="event-type">Conversion event</Text>
                            <Text display="inline-flex" fontSize="18px" color="red.400" mr={2}>*</Text>
                            <Tooltip
                                borderRadius={5}
                                label={
                                    <Box fontSize="sm" p={'5px'}>
                                        <Text>
                                            Choose a conversion event associated with your Meta pixel. A conversion event is an action that you want People to take when they see your ads.
                                        </Text>
                                    </Box>
                                }
                                fontSize="md"
                            >
                                <InfoOutlineIcon pointerEvents="all" />
                            </Tooltip>
                        </Flex>

                        <Box>
                            {isCustomListLoading || isStatusListLoading ? (
                                <Flex justifyContent={'center'} alignItems={'center'} mt={2}>
                                    <SkeletonText width="100%" noOfLines={2} skeletonHeight={2}/>
                                </Flex>
                            ) : (
                                <Select
                                    id="event-type"
                                    value={selectedEventType}
                                    onChange={handleEventTypeChange}
                                    size={'sm'}
                                    isDisabled={!selectedPixelId}
                                    borderRadius={'md'}
                                >
                                    <option value="" disabled>Select Event Type</option>
                                    {/* Only show "purchase" if optimization goal is 'VALUE' */}
                                    {adSetFormik?.values?.optimization_goal === 'VALUE' ? (
                                        <option value="PURCHASE">Purchase</option>
                                    ) : (
                                        filterEventsByPixel()?.map((eventOption, index) => (
                                            <option
                                                key={`${eventOption?.value}-${index}`}
                                                value={eventOption?.category === 'Custom' ? eventOption?.custom_conversion_id : eventOption?.value}
                                                disabled={!eventOption?.available}
                                                data-option={JSON.stringify(eventOption)}
                                            >
                                                {eventOption?.label}
                                            </option>
                                        ))
                                    )}
                                </Select>
                            )}
                        </Box>

                        {eventTypeError && isSaving && (!selectedEventType || selectedEventType === '') && (
                            <Text color="red.500" fontSize="sm" mt={1}>
                                Event type is required.
                            </Text>
                        )}
                    </Box>
                </Flex>
            ) : (
                <Flex gap={'10px'}>
                    <InfoOutlineIcon pointerEvents="all" color={'red.500'} />
                    <Text fontSize={'12px'} color={'red.500'}>The Meta pixel is required. To publish, set up your pixel or switch to a different optimisation, such as landing page views</Text>
                </Flex>
            )}
        </Box>
    );
};

export default DataSetPixelSelector;
